import { createContext, useContext } from 'react';

import type { AdminProfile } from './session';

export interface AuthState {
  user: AdminProfile | null;
  /** True until the initial refresh settles, so guards do not flash the login page. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Drop the session locally without calling the API — for after a password
   * change or reset, when the server has already revoked every session.
   */
  clearSession: () => void;
  /** Swap in a fresh profile after the user edits their own details. */
  updateUser: (profile: AdminProfile) => void;
  /** SUPER_ADMIN passes every check, mirroring `PermissionsGuard` on the server. */
  can: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthState | null>(null);

/**
 * Kept out of `auth.tsx` so that file exports only a component and Fast Refresh
 * can still hot-swap the provider — the same split as `lib/status.ts`.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }

  return context;
}
